(function () {
  "use strict";

  // Loaded once from layout/theme.liquid (not from inside the cart-drawer
  // section), so it survives every drawer re-render untouched. The widget's
  // own markup lives in sections/cart-drawer.liquid and gets destroyed/
  // recreated on every cart change (theme.js replaces the whole <cart-drawer>
  // content), so we never hold direct references to it — everything below is
  // event delegation on `document` plus fresh DOM lookups at click time.

  // Written by marketing through the discount's own metafield sync (or, for
  // now, directly on the shop metafield): a JSON object keyed by discount
  // code (lowercased): { "<code>": { variantId, quantity, triggerVariantIds, minQuantity } }.
  // Falls back to the legacy Flow-era format (one "code|variantId|quantity|trigger1,trigger2|minQuantity"
  // line per code) for promos created before the JSON migration.
  function parseGiftPromoConfig(raw) {
    var configByCode = {};
    if (!raw) return configByCode;

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      return parseLegacyGiftPromoConfig(raw);
    }

    if (!parsed || typeof parsed !== "object") {
      return parseLegacyGiftPromoConfig(raw);
    }

    Object.keys(parsed).forEach(function (code) {
      var config = parsed[code] || {};
      if (!config.variantId || !config.quantity) return;

      configByCode[code.toLowerCase()] = {
        variantId: idToNumericId(config.variantId),
        quantity: Number(config.quantity),
        triggerVariantIds: (Array.isArray(config.triggerVariantIds) ? config.triggerVariantIds : [])
          .map(function (id) {
            return idToNumericId(id);
          })
          .filter(Boolean),
        minQuantity: Number(config.minQuantity) || 0,
      };
    });

    return configByCode;
  }

  function parseLegacyGiftPromoConfig(raw) {
    var configByCode = {};

    raw
      .split("\n")
      .map(function (line) {
        return line.trim();
      })
      .filter(Boolean)
      .forEach(function (line) {
        var parts = line.split("|").map(function (part) {
          return part.trim();
        });
        var code = parts[0];
        var variantId = parts[1];
        var quantity = parts[2];
        var triggers = parts[3];
        var minQuantity = parts[4];
        if (!code || !variantId || !quantity) return;

        configByCode[code.toLowerCase()] = {
          variantId: idToNumericId(variantId),
          quantity: Number(quantity),
          triggerVariantIds: (triggers || "")
            .split(",")
            .map(function (id) {
              return idToNumericId(id.trim());
            })
            .filter(Boolean),
          minQuantity: Number(minQuantity) || 0,
        };
      });

    return configByCode;
  }

  // The Cart AJAX API (/cart.js, /cart/add.js) works with the numeric variant
  // id, not the "gid://shopify/ProductVariant/123" GID stored in the metafield.
  function idToNumericId(gid) {
    if (!gid) return null;
    var match = /(\d+)\s*$/.exec(gid);
    return match ? match[1] : gid;
  }

  function isEligible(config, cartLines) {
    if (!config.triggerVariantIds.length || !config.minQuantity) {
      return true;
    }

    var triggerQuantity = cartLines.reduce(function (sum, line) {
      var lineVariantId = String(line.variant_id);
      return config.triggerVariantIds.indexOf(lineVariantId) !== -1
        ? sum + line.quantity
        : sum;
    }, 0);

    return triggerQuantity >= config.minQuantity;
  }

  function fetchCart() {
    return fetch(window.Shopify.routes.root + "cart.js", { headers: { Accept: "application/json" } }).then(
      function (res) {
        return res.json();
      }
    );
  }

  function addGiftLine(variantId, quantity) {
    return fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] }),
    });
  }

  function applyDiscountCode(code) {
    return fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ discount: code }),
    }).then(function (res) {
      return res.json();
    });
  }

  function removeGiftLine(variantId) {
    return fetch(window.Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: variantId, quantity: 0 }),
    });
  }

  // Our own eligibility check (isEligible above) only understands trigger
  // variant + minimum quantity conditions. Some discounts use conditions we
  // can't pre-validate on our side (e.g. "minimum purchase amount") — Shopify
  // still accepts the code (no error from cart/update.js) but simply never
  // discounts the gift line if its own condition isn't actually met. So we
  // always double-check the real cart returned by cart/update.js: if the
  // gift line wasn't actually reduced to 0, the discount didn't really apply.
  function wasGiftDiscounted(cart, config) {
    var giftLine = cart.items.filter(function (line) {
      return String(line.variant_id) === config.variantId;
    })[0];

    return !!giftLine && giftLine.final_line_price < giftLine.original_line_price;
  }

  function setStatus(widget, message, tone) {
    var statusEl = widget.querySelector("[data-gift-code-status]");
    if (!statusEl) return;
    statusEl.textContent = message;
    statusEl.hidden = !message;
    if (tone) {
      statusEl.setAttribute("data-tone", tone);
    } else {
      statusEl.removeAttribute("data-tone");
    }
  }

  // theme.js rebuilds the ENTIRE <cart-drawer> content from a fresh server
  // render on every "cart:refresh"/"cart:change" (see refreshCart_fn in
  // assets/theme.js), which wipes out any status text we had just set on the
  // old (now-destroyed) widget node. So when we need to refresh the drawer
  // AND keep a status message visible, we stash the message and re-apply it
  // once to the NEW widget node, right after the drawer finishes refreshing
  // (theme.js dispatches "cart-drawer:refreshed" — bubbles up to document —
  // at the end of that same replace, see connectif-cart-sync.js for the same
  // pattern).
  var pendingStatus = null;
  document.addEventListener("cart-drawer:refreshed", function () {
    if (!pendingStatus) return;
    var widget = document.querySelector("[data-gift-code-widget]");
    if (widget) {
      setStatus(widget, pendingStatus.message, pendingStatus.tone);
    }
    pendingStatus = null;
  });

  function refreshCartAndKeepStatus(message, tone) {
    pendingStatus = { message: message, tone: tone };
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  function handleSubmit(widget) {
    var dataEl = widget.querySelector("[data-gift-code-widget-data]");
    var input = widget.querySelector('input[name="gift_code"]');
    var code = ((input && input.value) || "").trim().toLowerCase();
    if (!code || !dataEl) return;

    var giftPromoConfig = {};
    try {
      var rawConfig = JSON.parse(dataEl.textContent || "null");
      // A JSON-type metafield is emitted as an object, not a string.
      if (rawConfig && typeof rawConfig === "object") rawConfig = JSON.stringify(rawConfig);
      giftPromoConfig = parseGiftPromoConfig(rawConfig);
    } catch (err) {
      console.error("[gift-code-widget] Could not parse gift promo config", err);
    }
    var config = giftPromoConfig[code];

    setStatus(widget, "");

    if (!config) {
      // Not a gift code: apply it as a regular discount code.
      applyDiscountCode(code)
        .then(function (cart) {
          var applied = (cart.discount_codes || []).some(function (d) {
            return d.code.toLowerCase() === code && d.applicable;
          });
          if (!applied) {
            setStatus(widget, widget.dataset.notEligibleMessage, "error");
            return;
          }
          refreshCartAndKeepStatus("", null);
        })
        .catch(function (err) {
          console.error("[gift-code-widget] Failed to apply discount code", err);
          setStatus(widget, widget.dataset.notEligibleMessage, "error");
        });
      return;
    }

    fetchCart()
      .then(function (cart) {
        if (!isEligible(config, cart.items)) {
          setStatus(widget, widget.dataset.notEligibleMessage, "error");
          return null;
        }

        var giftAlreadyInCart = cart.items.some(function (line) {
          return String(line.variant_id) === config.variantId;
        });

        var addGift = giftAlreadyInCart
          ? Promise.resolve()
          : addGiftLine(config.variantId, config.quantity).then(function (res) {
              if (!res.ok) throw new Error("cart/add.js failed: " + res.status);
            });

        return addGift.then(function () {
          return applyDiscountCode(code);
        }).then(function (updatedCart) {
          if (wasGiftDiscounted(updatedCart, config)) {
            return {discounted: true};
          }

          // Shopify accepted the code (no error) but its own condition on the
          // discount wasn't actually met, so the gift line stayed full price.
          // Roll back the gift we just added — but only if WE added it; if it
          // was already in the customer's cart before, leave it alone.
          var rollback = giftAlreadyInCart ? Promise.resolve() : removeGiftLine(config.variantId);
          return rollback.then(function () {
            return {discounted: false};
          });
        });
      })
      .then(function (result) {
        if (result === null) return;

        if (!result.discounted) {
          // Nothing worth refreshing for: either nothing was touched (the
          // gift was already in the cart) or we added it and rolled it right
          // back — net zero change — so the current widget node is still the
          // one on screen and we can just set the message on it directly.
          setStatus(widget, widget.dataset.notEligibleMessage, "error");
          return;
        }

        // The gift line is now genuinely discounted — a real, visible cart
        // change, so the drawer needs to refresh. Native refresh hook already
        // used by this theme's own <cart-drawer> component (assets/theme.js)
        // and by connectif-cart-sync.js; we stash the success message so it
        // survives that refresh instead of vanishing with the old widget node.
        refreshCartAndKeepStatus(widget.dataset.appliedMessage, "success");
      })
      .catch(function (err) {
        console.error("[gift-code-widget] Failed to apply gift code", err);
        setStatus(widget, widget.dataset.notEligibleMessage, "error");
      });
  }

  document.addEventListener("click", function (event) {
    var button = event.target.closest('[data-gift-code-widget] button[name="gift_code_apply"]');
    if (!button) return;
    var widget = button.closest("[data-gift-code-widget]");
    if (!widget) return;
    event.preventDefault();
    handleSubmit(widget);
  });

  // Remove one applied discount code: cart/update.js replaces the whole code
  // list, so we resend the remaining ones. Removing a gift code leaves the gift
  // line in the cart at full price (customer can remove it with "Quitar").
  document.addEventListener("click", function (event) {
    var button = event.target.closest("[data-remove-discount-code]");
    if (!button) return;
    event.preventDefault();
    var chip = button.closest("[data-applied-discount-code]");
    var removed = chip.getAttribute("data-applied-discount-code");
    var remaining = Array.prototype.slice
      .call(document.querySelectorAll("[data-applied-discount-code]"))
      .map(function (el) {
        return el.getAttribute("data-applied-discount-code");
      })
      .filter(function (c) {
        return c !== removed;
      });

    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ discount: remaining.join(",") }),
    })
      .then(function () {
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      })
      .catch(function (err) {
        console.error("[gift-code-widget] Failed to remove discount code", err);
      });
  });

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Enter") return;
    var input = event.target.closest('[data-gift-code-widget] input[name="gift_code"]');
    if (!input) return;
    var widget = input.closest("[data-gift-code-widget]");
    if (!widget) return;
    // Prevent the outer <form class="cart-drawer__footer"> from submitting
    // (and navigating to checkout) when the customer presses Enter here.
    event.preventDefault();
    handleSubmit(widget);
  });
})();
